import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '../services/api';
import { API } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for auth-expired events from the Axios interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(false);
    };
    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    // Auto-detect and save timezone on login
    _syncTimezone();
    return data;
  };

  const register = async (email, password, name, inviteToken, referralCode) => {
    const payload = { email, password, name };
    if (inviteToken) payload.invite_token = inviteToken;
    if (referralCode) payload.referral_code = referralCode;
    const { data } = await axios.post(`${API}/auth/register`, payload);
    setUser(data);
    // Auto-detect and save timezone on register
    _syncTimezone();
    return data;
  };

  const _syncTimezone = async () => {
    try {
      // Only set the user's timezone if they don't already have one saved.
      // Browser detection is a "last resort" — user's explicit choice in
      // /profile always wins.
      const { data: profile } = await axios.get(`${API}/profile`).catch(() => ({ data: null }));
      if (profile && profile.timezone) return;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        await axios.put(`${API}/profile/timezone`, { timezone: tz });
      }
    } catch { /* ignore — non-critical */ }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Proceed with local cleanup even if server logout fails
    }
    // Clear activation "fired" flags so the next user's firsts fire cleanly.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('kindra_act_fired__'))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* noop */ }
    // Clear in-memory SWR cache so the next user doesn't see this user's data.
    try {
      const { clearSWRCache } = await import('../services/useSWRCache');
      clearSWRCache();
    } catch { /* noop */ }
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  // ─── Session tracking for founder analytics ───
  const sessionRef = useRef(null);
  useEffect(() => {
    if (!user || user === false) return;
    const sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionRef.current = sid;
    const ua = navigator.userAgent || '';
    axios.post(`${API}/founder/track-session`, { action: 'start', session_id: sid, user_agent: ua, route: window.location.pathname }).catch(() => {});

    // Only heartbeat when tab is visible (Page Visibility API)
    let hbInterval = null;
    const startHeartbeat = () => {
      if (hbInterval) return;
      hbInterval = setInterval(() => {
        axios.post(`${API}/founder/track-session`, { action: 'heartbeat', session_id: sid, route: window.location.pathname }).catch(() => {});
      }, 60000);
    };
    const stopHeartbeat = () => {
      if (hbInterval) { clearInterval(hbInterval); hbInterval = null; }
    };
    const onVisChange = () => {
      if (document.visibilityState === 'visible') startHeartbeat();
      else stopHeartbeat();
    };
    document.addEventListener('visibilitychange', onVisChange);
    if (document.visibilityState === 'visible') startHeartbeat();

    // Session end on unload (sendBeacon for reliability)
    const onUnload = () => {
      const body = JSON.stringify({ action: 'end', session_id: sid });
      try { navigator.sendBeacon?.(`${API}/founder/track-session`, new Blob([body], { type: 'application/json' })); } catch {}
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);

    return () => {
      stopHeartbeat();
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
      axios.post(`${API}/founder/track-session`, { action: 'end', session_id: sid }).catch(() => {});
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
